import { Badge, Button, Divider, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Tag } from "@chakra-ui/react"
import dayjs from "dayjs";
import { Util } from "../../dal/util";
import config  from './../../config';
import { useEffect, useState } from "react";
import ImagePreview from '../ImagePreview';

interface AbsenceDetailInfo {
    AbsenceType: string;
    Period: string;
}

interface AbsenceRecordInfo {
    StudentID: number;
    SchoolYear: number;
    Semester: number;
    OccurDate: string;
    AbsenceDetail: AbsenceDetailInfo[];
}

interface StudentAbsenceMatchInfo {
    studentID: number;
    absenceRecords: AbsenceRecordInfo[];
    matchedAbsenceRecords: AbsenceRecordInfo[];
    isMatched: boolean;
    compareRequired: boolean;
}

interface LeaveDateAndPeriodInfo {
    d: string;
    periods: string[];
}

interface LeaveStudentInfo {
    studentID: number;
    studentNumber: string;
    className: string;
    name: string;
}

interface LeaveAppModalRecord {
    status?: string;
    is_sync_success?: boolean | null;
    school_year?: number;
    semester?: number;
    apply_time?: string;
    applicant_role?: string;
    application_no?: string;
    current_stage_name?: string;
    leaveDateAndPeriod?: LeaveDateAndPeriodInfo[];
    absence_type?: string;
    reason?: string;
    attachment_info?: { fileid: string; filename?: string };
    parent_sign_info?: { fileid: string };
    parent_sign_time?: string;
    need_parent_signature?: boolean;
    approveHistory?: any[];
    studentAbsenceMatchInfo?: StudentAbsenceMatchInfo[];
}

interface props {
    selectedLeaveApp:LeaveAppModalRecord;
    selectedLeaveStu:LeaveStudentInfo;
    isModalOpen:boolean;
    isSystemAdmin?: boolean;
    onModalClose:()=>void;
}

const dedupePreserveOrder = (items: string[]) => Array.from(new Set(items));

const buildLeavePeriodMap = (leaveDateAndPeriod: LeaveDateAndPeriodInfo[] = []) => {
    const leavePeriodMap = new Map<string, string[]>();

    leaveDateAndPeriod.forEach(({ d, periods }) => {
        const currentPeriods = leavePeriodMap.get(d) ?? [];
        leavePeriodMap.set(d, dedupePreserveOrder(currentPeriods.concat(periods)));
    });

    return leavePeriodMap;
}

const buildAbsenceDetailMap = (absenceRecords: AbsenceRecordInfo[] = []) => {
    const absenceDetailMap = new Map<string, string[]>();

    absenceRecords.forEach((record) => {
        const matchedDetails = record.AbsenceDetail
            .map((detail) => `${detail.Period}|${detail.AbsenceType}`);

        if (matchedDetails.length === 0) {
            return;
        }

        const currentDetails = absenceDetailMap.get(record.OccurDate) ?? [];
        absenceDetailMap.set(record.OccurDate, dedupePreserveOrder(currentDetails.concat(matchedDetails)));
    });

    return absenceDetailMap;
}

const getStudentAbsenceMatchInfo = (
    selectedLeaveApp?: LeaveAppModalRecord,
    studentID?: number
) => selectedLeaveApp?.studentAbsenceMatchInfo?.find((item) => item.studentID === studentID);

const formatAbsenceDetail = (details: string[]) => {
    if (details.length === 0) {
        return '無';
    }

    return details
        .map((detail) => {
            const [period, absenceType] = detail.split('|');
            return `${period}(${absenceType})`;
        })
        .join('、');
}

export const LeaveAppModal=({selectedLeaveApp,selectedLeaveStu,isModalOpen,isSystemAdmin,onModalClose}:props)=>{
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
    const [statusCSS, setStatusCSS] = useState<string>("");

    const weekDays=['日','一','二','三','四','五','六'];
    const leaveDateAndPeriod = selectedLeaveApp?.leaveDateAndPeriod ?? [];
    const approveHistory = selectedLeaveApp?.approveHistory ?? [];
    const statusText = Util.convertStatusToText(selectedLeaveApp?.status ?? '', !!selectedLeaveApp?.is_sync_success, selectedLeaveApp?.semester ?? 0);
    const applicantRoleText = Util.convertRoleToText(selectedLeaveApp?.applicant_role ?? '');
    const selectedStudentAbsenceInfo = getStudentAbsenceMatchInfo(selectedLeaveApp, selectedLeaveStu?.studentID);
    const leavePeriodMap = buildLeavePeriodMap(leaveDateAndPeriod);
    const leaveDetailMap = new Map(
        Array.from(leavePeriodMap.entries()).map(([date, periods]) => [
            date,
            dedupePreserveOrder(periods.map((period) => `${period}|${selectedLeaveApp?.absence_type ?? ''}`))
        ])
    );
    const absenceDetailMap = buildAbsenceDetailMap(selectedStudentAbsenceInfo?.matchedAbsenceRecords);
    const absenceComparison = Array.from(leavePeriodMap.entries()).map(([date, expectedPeriods]) => {
        const expectedDetails = leaveDetailMap.get(date) ?? [];
        const actualDetails = absenceDetailMap.get(date) ?? [];
        // 檢查請假單的每個節次+類型是否都在校務缺曠中（校務缺曠可以有額外的節次）
        const isMatched = expectedDetails.length > 0 && expectedDetails.every((detail: string) => actualDetails.includes(detail));
        return {
            date,
            expectedPeriods,
            expectedDetails,
            actualDetails,
            isMatched
        };
    });

    const handleImageClick = (src: string) => {
        setSelectedImage(src);
        setIsPreviewOpen(true);
      };  
      const hideImagePreview = () => {
        setSelectedImage(null);
        setIsPreviewOpen(false);
      };
      useEffect(() => {
        setStatusCSS("justify-center me-2 !text-white whitespace-nowrap "+Util.calculateProgressText(selectedLeaveApp).progressTextCSS);    
      },[selectedLeaveApp])

    return(
        <Modal
            closeOnOverlayClick={false}
            isOpen={isModalOpen}
            onClose={onModalClose}>
            <ModalOverlay />
            <ModalContent maxW={{ base: "100%", sm: "400px", md: "600px", lg: "800px" }}>
                <ModalHeader className='!font-normal'></ModalHeader>
                <ModalCloseButton />

                <ModalBody pb={6}>
                <div className='text-left'>
                    <div className='flex items-baseline gap-x-2'>
                        <div className='text-xl'>{selectedLeaveStu?.studentNumber}</div>
                        <div className='text-xl font-semibold'>{selectedLeaveStu?.className} {selectedLeaveStu?.name}</div>
                    </div>
                    <div className="flex items-center">
                    <Tag size='md' h={8} className={statusCSS}> {statusText}</Tag>
                    {/* <Tag size='md' className="w-[58px] justify-center me-2 !text-white bg-return">退回</Tag> */}
                    {/* <Tag size='md' className="w-[58px] justify-center me-2 !text-white bg-ed">已完成</Tag> */}
                    <div className="text-sm">
                        <div>申請時間：{ selectedLeaveApp?.apply_time ? dayjs(selectedLeaveApp.apply_time).format('YYYY/MM/DD HH:mm') : ''} 由{ applicantRoleText}提出</div>
                        <div>學年學期：{selectedLeaveApp?.school_year ?? '無'} 學年度 {selectedLeaveApp?.semester ? (selectedLeaveApp.semester === 3 ? '寒輔' : selectedLeaveApp.semester === 4 ? '暑輔' : '第'+ selectedLeaveApp.semester + '學期') : '無'}</div>
                        <div>請假單號：{selectedLeaveApp?.application_no}</div>
                    </div>
                    </div>
                    <div className="text-xl font-semibold  mt-3">待簽核：<span className=''>{selectedLeaveApp?.current_stage_name}</span></div>
                    <Divider my={2} />

                    <div className="grid grid-cols-1 lg:grid-cols-3 grid-self gap-3 ">
                        <div>
                            <div className='text-sm font-semibold my-1' style={{width: '200px'}}>請假日期</div>
                            { leaveDateAndPeriod.map( (dp: any) => (
                            <div key={dp.d}>
                                <div className='text-sm font-semibold mt-2 mb-1'>{ dayjs(dp.d).format('YYYY/MM/DD')}({weekDays[dayjs(dp.d).day()]})</div>
                                <div>{dp.periods.join('、')}</div>
                            </div>
                            ))}
                            
                        </div>
                        <Divider orientation='vertical' className='hidden lg:block' />
                        <Divider className='block lg:hidden' />
                        <div>
                            <div className=''>
                            <div className='text-sm font-semibold my-1'>假別</div>
                            <div>{selectedLeaveApp?.absence_type}</div>
                            </div>
                            <Divider my={2} />
                            <div>
                            <div className='text-sm font-semibold my-1'>事由</div>
                            <div>{selectedLeaveApp?.reason}</div>
                            </div>
                        </div>
                    </div>
                    <Divider my={2} />
                    {selectedStudentAbsenceInfo?.compareRequired && (isSystemAdmin) && (
                        <>
                            <div>
                                <div className='flex items-center gap-2 mb-2'>
                                    <div className='text-sm font-semibold'>校務缺曠比對</div>
                                    <Badge colorScheme={selectedStudentAbsenceInfo.isMatched ? 'green' : 'red'}>
                                        {selectedStudentAbsenceInfo.isMatched ? '一致' : '不一致'}
                                    </Badge>
                                </div>
                                <div className='text-sm text-gray-600 mb-3'>
                                    只比對此假單請假日期、請假學年度學期內，校務系統相同日期的缺曠資料，並同時檢查節次與假別。
                                </div>
                                {absenceComparison.map((item) => (
                                    <div key={item.date} className='mb-3 rounded border border-gray-200 p-3'>
                                        <div className='flex items-center gap-2 mb-2'>
                                            <div className='font-semibold'>{dayjs(item.date).format('YYYY/MM/DD')}({weekDays[dayjs(item.date).day()]})</div>
                                            <Badge colorScheme={item.isMatched ? 'green' : 'red'}>
                                                {item.isMatched ? '一致' : '不一致'}
                                            </Badge>
                                        </div>
                                        <div className='text-sm'>請假節次：{item.expectedPeriods.length > 0 ? item.expectedPeriods.join('、') : '無'}</div>
                                        <div className='text-sm'>請假假別：{selectedLeaveApp?.absence_type ?? '無'}</div>
                                        <div className='text-sm'>校務缺曠：{formatAbsenceDetail(item.actualDetails)}</div>
                                    </div>
                                ))}
                                {!selectedStudentAbsenceInfo?.isMatched && selectedStudentAbsenceInfo?.matchedAbsenceRecords?.length === 0 && (
                                    <div className='text-sm text-red-500'>未匹配原因：請假日期內查無校務缺曠紀錄。</div>
                                )}
                                {!selectedStudentAbsenceInfo?.isMatched && selectedStudentAbsenceInfo?.matchedAbsenceRecords?.length !== 0 && absenceComparison.some((item) => !item.isMatched) && (
                                    <div className='text-sm text-red-500'>未匹配原因：同日期的請假節次或假別與校務缺曠資料不完全一致。</div>
                                )}
                            </div>
                            <Divider my={2} />
                        </>
                    )}
                    <div className="grid grid-cols-1 lg:grid-cols-3 grid-self gap-3 ">
                    <div>
                        {(selectedLeaveApp?.attachment_info) ?
                        <>
                        <div className='text-sm font-semibold my-1'>附件</div>
                        <div className='w-20'>
                        <img src={config.storageURL+'/'+selectedLeaveApp.attachment_info.fileid} alt="Attachment" className='mx-auto imgs'
                        onClick={() => handleImageClick(config.storageURL+'/'+selectedLeaveApp?.attachment_info?.fileid)} />
                        </div>                  
                        </>
                        :
                        <div className='whitespace-nowrap'>無附件</div>
                        }
                    </div>
                    <Divider className='block lg:hidden' />
                    <Divider orientation='vertical' className='hidden lg:block' />
                    <div>
                        {(selectedLeaveApp?.parent_sign_info) ?
                        <>
                        <div className='text-sm font-semibold my-1'>家長簽名</div>
                        <div className='w-20 signbg'>
                            <img src={config.storageURL+'/'+selectedLeaveApp.parent_sign_info.fileid} alt="Signature" className='mx-auto imgs'  onClick={() => handleImageClick(config.storageURL+'/'+selectedLeaveApp?.parent_sign_info?.fileid)} /> 
                        </div>
                        {selectedLeaveApp?.parent_sign_time &&
                            <div className='whitespace-nowrap'>{dayjs(selectedLeaveApp?.parent_sign_time).format('YYYY/MM/DD HH:mm')}</div>
                        }
                        </>:
                        (selectedLeaveApp?.need_parent_signature) ?
                        <div className='whitespace-nowrap'>家長尚未簽名</div>
                        :
                        <div className='whitespace-nowrap'>不需家長簽名</div>
                        }
                    </div>
                    </div>
                    {selectedImage && isPreviewOpen && (
                        <ImagePreview src={selectedImage} onClose={hideImagePreview} />
                    )}
                    <Divider my={2} />
                    <div className=' mb-6'>
                        <div className='text-sm font-semibold my-1'>審查回覆</div>


                    {(() => {
                      const histories = [];
                      for (const aph of approveHistory|| []) {
                        if (aph.status === 'rejected') {
                          histories.push(
                <>
                            <div>{aph.stage_name}：<span className="text-xl text-red-500 font-semibold">{ Util.convertApproveToText(aph.status)}</span><span> {aph.comment}</span></div>                  
                            <div className="text-sm mb-2">{dayjs(aph.approve_time).format('YYYY/MM/DD HH:mm:ss')}</div>
                        </>
                          );
                          break;
                        } else {
                          histories.push(
            <>
                            <div>{aph.stage_name}：{ Util.convertApproveToText(aph.status)}</div>
                            {(aph.status==='approved') && <div className="text-sm mb-2">{dayjs(aph.approve_time).format('YYYY/MM/DD HH:mm:ss')}</div>}
                        </>
                          );
                        }
                      }
                      return histories;
                    })()}

                    </div>
                    </div>
                </ModalBody>

                <ModalFooter className='gap-4'>
                    <Button colorScheme='orange' variant='outline' className="mr-auto w-full" onClick={onModalClose}>返回</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    )
}